'use client';

import { Form, Formik } from 'formik';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { Button } from 'src/general/components/Button/Button';
import { CircularProgress } from 'src/general/components/CircularProgress/CircularProgress';
import { LocationObject } from 'src/general/components/SetLocationPopup/EnterLocationScreen/SetLocation/SetLocation.types';
import { FormikInput } from 'src/general/components/forms/FormikInput/FormikInput';
import { pushCarmaEvent } from 'src/general/helpers/carmaCritical';
import { sendLocationEnquiry } from 'src/services/LeadService';
import { pushToDataLayer, pushToDataLayerWithEnhancedConversion } from 'src/utils/pushToDataLayer';

import classes from './EnterEmail.module.scss';

interface Props {
  location: LocationObject;
  placeholder: ReactNode;
  submitButton: ReactNode;
  successMessage: ReactNode;
}

export const EnterEmail: FC<Props> = ({ location, placeholder, submitButton, successMessage }) => {
  const onSubmit = useCallback(
    async ({ email }: { email: string }) => {
      try {
        await sendLocationEnquiry({
          email,
          post_code: location.post_code,
          suburb: location.name,
          state: location.state,
        });
        pushCarmaEvent({
          event: 'form_submission',
          metadata: {
            email,
            component_name: 'SetHandoverLocationPopup',
          },
        });
        pushToDataLayer({
          event: 'set_handover_location_email_submission',
          email,
        });
      } catch (e) {
        // TODO Handle error message
      }
      setEmailSubmitted(email);
    },
    [location],
  );

  const [emailSubmitted, setEmailSubmitted] = useState<string | null>(null);

  const getValidatesSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email address does not exist or invalid')
      .email('Email address does not exist or invalid'),
  });

  useEffect(() => {
    if (emailSubmitted) {
      pushToDataLayerWithEnhancedConversion(emailSubmitted, undefined, {
        event: `pickup_location-email`,
      });
    }
  }, [emailSubmitted]);

  return emailSubmitted ? (
    <div className={classes.successWrapper}>
      <img src="/images/components/EnterEmail/Icon.svg" alt="" />
      <span className={classes.successMessage}>{successMessage}</span>
    </div>
  ) : (
    <Formik enableReinitialize initialValues={{ email: '' }} validationSchema={getValidatesSchema} onSubmit={onSubmit}>
      {({ isSubmitting }) => (
        <Form autoComplete="off" className={classes.form}>
          <div className={classes.flexRow}>
            <FormikInput type="text" name="email" label={placeholder} className={classes.emailInput} />
            <Button type="submit" data-testid="EnterEmail-submit" className={classes.button}>
              {isSubmitting ? <CircularProgress /> : <span>{submitButton}</span>}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
